import useSWR from 'swr';
import { fetcher } from '@/lib/utils';
import type { SubscriptionData } from '@/lib/types';

interface UseSubscriptionOptions {
  userId?: string;
}

export function useSubscription({ userId }: UseSubscriptionOptions) {
  const {
    data: subscriptionData,
    isLoading: loadingSubscription,
    mutate: mutateSubscription,
  } = useSWR<SubscriptionData>(
    userId ? `/customer/api/subscription/check` : null,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 60000, // 1 minuto de cache
      onErrorRetry: (error, _key, _config, revalidate, { retryCount }) => {
        // Never retry on auth errors — session is gone, retrying is pointless
        if (error.status === 401 || error.status === 403) return;
        // Max 3 retries for other errors
        if (retryCount >= 3) return;
        setTimeout(() => revalidate({ retryCount }), 5000 * (retryCount + 1));
      },
    },
  );

  const activeSubscription = subscriptionData?.subscription ?? null;
  const subscriptionProvider: 'stripe' | 'revenuecat' | null =
    activeSubscription
      ? 'stripeSubscriptionId' in activeSubscription
        ? 'stripe'
        : 'revenuecatOriginalTransactionId' in activeSubscription
          ? 'revenuecat'
          : null
      : null;

  const isRevenueCat = subscriptionProvider === 'revenuecat';
  const isStripe = subscriptionProvider === 'stripe';

  return {
    subscriptionData,
    loadingSubscription,
    mutateSubscription,
    activeSubscription,
    subscriptionProvider,
    isRevenueCat,
    isStripe,
  };
}
